import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { VisionData, HealthData, QueryParams, QueryResponse } from '../../types';

// 模拟视力筛查数据
const mockVisionData: VisionData[] = [
  {
    id: 'v1',
    studentName: '张三',
    studentId: 'stu001',
    schoolName: '第一小学',
    schoolId: 'school_001',
    grade: '三年级',
    class: '1班',
    gender: 'male',
    age: 9,
    birthday: '2015-03-15',
    checkDate: '2024-11-15',
    leftVision: 5.0,
    rightVision: 4.9,
    leftSph: 0.0,
    leftCyl: 0.0,
    leftAxis: 0,
    rightSph: -0.25,
    rightCyl: 0.0,
    rightAxis: 0,
    isAbnormal: false,
    suggestion: '视力正常，继续保持',
    recorder: '李医生',
    createdAt: '2024-11-15T10:30:00',
    updatedAt: '2024-11-15T10:30:00',
  },
  {
    id: 'v2',
    studentName: '李四',
    studentId: 'stu002',
    schoolName: '第一小学',
    schoolId: 'school_001',
    grade: '四年级',
    class: '2班',
    gender: 'female',
    age: 10,
    birthday: '2014-07-20',
    checkDate: '2024-11-20',
    leftVision: 4.6,
    rightVision: 4.5,
    leftSph: -1.5,
    leftCyl: -0.5,
    leftAxis: 180,
    rightSph: -1.75,
    rightCyl: -0.75,
    rightAxis: 175,
    isAbnormal: true,
    abnormalType: '近视',
    suggestion: '建议佩戴眼镜，定期复查',
    recorder: '王医生',
    createdAt: '2024-11-20T14:20:00',
    updatedAt: '2024-11-20T14:20:00',
  },
];

// 模拟健康监测数据
const mockHealthData: HealthData[] = [
  {
    id: 'h1',
    residentName: '王五',
    residentId: 'res001',
    communityName: '阳光社区',
    communityId: 'community_001',
    gender: 'male',
    age: 45,
    birthday: '1979-05-10',
    checkDate: '2024-11-18',
    bloodPressure: {
      systolic: 125,
      diastolic: 82,
    },
    bloodSugar: {
      fasting: 5.2,
      postprandial: 7.8,
    },
    bloodLipid: {
      cholesterol: 4.8,
      triglyceride: 1.5,
      hdl: 1.2,
      ldl: 2.8,
    },
    heartRate: 72,
    height: 175,
    weight: 70,
    bmi: 22.9,
    isAbnormal: false,
    suggestion: '各项指标正常，继续保持',
    recorder: '张护士',
    createdAt: '2024-11-18T09:15:00',
    updatedAt: '2024-11-18T09:15:00',
  },
  {
    id: 'h2',
    residentName: '赵六',
    residentId: 'res002',
    communityName: '阳光社区',
    communityId: 'community_001',
    gender: 'female',
    age: 62,
    birthday: '1962-08-25',
    checkDate: '2024-11-22',
    bloodPressure: {
      systolic: 145,
      diastolic: 92,
    },
    bloodSugar: {
      fasting: 6.8,
      postprandial: 10.2,
    },
    bloodLipid: {
      cholesterol: 6.2,
      triglyceride: 2.3,
      hdl: 1.0,
      ldl: 3.9,
    },
    heartRate: 85,
    height: 160,
    weight: 68,
    bmi: 26.6,
    isAbnormal: true,
    abnormalIndicators: ['高血压', '高血糖', '高血脂', '超重'],
    suggestion: '建议控制饮食，增加运动，定期复查',
    recorder: '李医生',
    createdAt: '2024-11-22T16:45:00',
    updatedAt: '2024-11-22T16:45:00',
  },
];

// 查询视力数据
export const queryVisionData = createAsyncThunk(
  'data/queryVision',
  async (params: QueryParams): Promise<QueryResponse<VisionData>> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        let filteredData = [...mockVisionData];

        // 应用筛选条件
        if (params.dateRange) {
          const [startDate, endDate] = params.dateRange;
          filteredData = filteredData.filter(item =>
            item.checkDate >= startDate && item.checkDate <= endDate
          );
        }

        if (params.ageRange) {
          const [minAge, maxAge] = params.ageRange;
          filteredData = filteredData.filter(item =>
            item.age >= minAge && item.age <= maxAge
          );
        }

        if (params.gender && params.gender !== 'all') {
          filteredData = filteredData.filter(item => item.gender === params.gender);
        }

        if (params.isAbnormal !== undefined) {
          filteredData = filteredData.filter(item => item.isAbnormal === params.isAbnormal);
        }

        if (params.keyword) {
          const keyword = params.keyword.toLowerCase();
          filteredData = filteredData.filter(item =>
            item.studentName.toLowerCase().includes(keyword) ||
            item.studentId.toLowerCase().includes(keyword) ||
            item.schoolName.toLowerCase().includes(keyword)
          );
        }

        // 分页
        const start = (params.page - 1) * params.pageSize;
        const end = start + params.pageSize;
        const paginatedData = filteredData.slice(start, end);

        resolve({
          data: paginatedData,
          total: filteredData.length,
          page: params.page,
          pageSize: params.pageSize,
        });
      }, 800);
    });
  }
);

// 查询健康监测数据
export const queryHealthData = createAsyncThunk(
  'data/queryHealth',
  async (params: QueryParams): Promise<QueryResponse<HealthData>> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        let filteredData = [...mockHealthData];

        // 应用筛选条件
        if (params.dateRange) {
          const [startDate, endDate] = params.dateRange;
          filteredData = filteredData.filter(item =>
            item.checkDate >= startDate && item.checkDate <= endDate
          );
        }

        if (params.ageRange) {
          const [minAge, maxAge] = params.ageRange;
          filteredData = filteredData.filter(item =>
            item.age >= minAge && item.age <= maxAge
          );
        }

        if (params.gender && params.gender !== 'all') {
          filteredData = filteredData.filter(item => item.gender === params.gender);
        }

        if (params.isAbnormal !== undefined) {
          filteredData = filteredData.filter(item => item.isAbnormal === params.isAbnormal);
        }

        if (params.keyword) {
          const keyword = params.keyword.toLowerCase();
          filteredData = filteredData.filter(item =>
            item.residentName.toLowerCase().includes(keyword) ||
            item.residentId.toLowerCase().includes(keyword) ||
            item.communityName.toLowerCase().includes(keyword)
          );
        }

        // 分页
        const start = (params.page - 1) * params.pageSize;
        const end = start + params.pageSize;
        const paginatedData = filteredData.slice(start, end);

        resolve({
          data: paginatedData,
          total: filteredData.length,
          page: params.page,
          pageSize: params.pageSize,
        });
      }, 800);
    });
  }
);

// 状态接口
interface DataState {
  visionData: VisionData[];
  healthData: HealthData[];
  queryResult: {
    vision: QueryResponse<VisionData> | null;
    health: QueryResponse<HealthData> | null;
  };
  loading: boolean;
  error: string | null;
}

// 初始状态
const initialState: DataState = {
  visionData: mockVisionData,
  healthData: mockHealthData,
  queryResult: {
    vision: null,
    health: null,
  },
  loading: false,
  error: null,
};

// 创建slice
const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    // 清除错误
    clearError: (state) => {
      state.error = null;
    },
    // 清除查询结果
    clearQueryResult: (state) => {
      state.queryResult = {
        vision: null,
        health: null,
      };
    },
    // 添加视力数据
    addVisionData: (state, action: PayloadAction<VisionData>) => {
      state.visionData.unshift(action.payload);
    },
    // 添加健康监测数据
    addHealthData: (state, action: PayloadAction<HealthData>) => {
      state.healthData.unshift(action.payload);
    },
    // 更新视力数据
    updateVisionData: (state, action: PayloadAction<VisionData>) => {
      const index = state.visionData.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.visionData[index] = action.payload;
      }
    },
    // 更新健康监测数据
    updateHealthData: (state, action: PayloadAction<HealthData>) => {
      const index = state.healthData.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.healthData[index] = action.payload;
      }
    },
    // 删除视力数据
    deleteVisionData: (state, action: PayloadAction<string>) => {
      state.visionData = state.visionData.filter(item => item.id !== action.payload);
    },
    // 删除健康监测数据
    deleteHealthData: (state, action: PayloadAction<string>) => {
      state.healthData = state.healthData.filter(item => item.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // 查询视力数据
      .addCase(queryVisionData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(queryVisionData.fulfilled, (state, action) => {
        state.loading = false;
        state.queryResult.vision = action.payload;
        state.error = null;
      })
      .addCase(queryVisionData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '查询视力数据失败';
      })

      // 查询健康监测数据
      .addCase(queryHealthData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(queryHealthData.fulfilled, (state, action) => {
        state.loading = false;
        state.queryResult.health = action.payload;
        state.error = null;
      })
      .addCase(queryHealthData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '查询健康监测数据失败';
      });
  },
});

export const {
  clearError,
  clearQueryResult,
  addVisionData,
  addHealthData,
  updateVisionData,
  updateHealthData,
  deleteVisionData,
  deleteHealthData,
} = dataSlice.actions;

export default dataSlice.reducer;